import { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  Users, 
  Home, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  LayoutDashboard,
  ShieldCheck,
  ScanLine,
  Wallet,
  BarChart3,
  Clock3,
  Banknote,
  HandHeart,
  ScrollText,
  FileText,
  Mail,
  BedDouble,
  Archive
} from 'lucide-react';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin/retreat/dashboard', icon: LayoutDashboard },
    { name: 'Attendees', href: '/admin/retreat/attendees', icon: Users },
    { name: '2027 Reservations', href: '/admin/retreat/reservations', icon: BedDouble },
    { name: 'Waitlist', href: '/admin/retreat/waitlist', icon: Clock3 },
    { name: 'Forms', href: '/admin/retreat/forms', icon: FileText },
    { name: 'Email', href: '/admin/retreat/email', icon: Mail },
    { name: 'Room Assignment', href: '/admin/retreat/rooms', icon: Home },
    { name: 'Check-In', href: '/admin/retreat/check-in', icon: ScanLine },
    { name: 'Reports', href: '/admin/retreat/reports', icon: BarChart3 },
    { name: 'Payments', href: '/admin/retreat/payments', icon: Banknote },
    { name: 'Volunteers', href: '/admin/retreat/volunteers', icon: HandHeart },
    { name: 'Ledger', href: '/admin/retreat/ledger', icon: Wallet },
    { name: 'Team & Users', href: '/admin/retreat/team', icon: ShieldCheck },
    { name: 'Audit Log', href: '/admin/retreat/audit-log', icon: ScrollText },
    { name: 'Season Archive', href: '/admin/retreat/archive', icon: Archive },
    { name: 'Settings', href: '/admin/retreat/settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar - Desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col print:hidden">
        <div className="flex flex-col flex-grow bg-gradient-to-b from-wine to-burgundy overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4 py-6">
            <h1 className="text-2xl font-bold text-white">ROJO Retreat</h1>
          </div>
          <nav className="mt-5 flex-1 px-2 pb-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all
                    ${
                      isActive
                        ? 'bg-white/20 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="flex-shrink-0 px-4 py-4 border-t border-white/20">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className="md:hidden">
        <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between bg-gradient-to-r from-wine to-burgundy px-4 py-4">
          <h1 className="text-xl font-bold text-white">ROJO Retreat</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-30 bg-gray-900/50" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-wine to-burgundy p-4 mt-16">
              <nav className="space-y-1">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`
                        group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all
                        ${
                          isActive
                            ? 'bg-white/20 text-white shadow-lg'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }
                      `}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white mt-4"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  Logout
                </Button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1 print:pl-0">
        <main className="flex-1 pt-16 md:pt-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;